import React from 'react';
import { useNavigate } from 'react-router-dom';
import './Instructions.css';

const EmailTaskIntro: React.FC = () => {
  const navigate = useNavigate();

  const handleBegin = () => {
    navigate('/email-task');
  };

  return (
    <div className="instructions-phase">
      <div className="instructions-content">
        <h1>📧 Email Security Task - Detailed Instructions</h1>

        <div className="instruction-section" style={{ backgroundColor: '#e3f2fd', padding: '1.5rem', borderRadius: '8px', marginBottom: '1.5rem' }}>
          <h2>🎯 THE SCENARIO</h2>
          <p style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '0.5rem' }}>
            Imagine you are at work, sitting at your computer.
          </p>
          <p style={{ fontSize: '15px' }}>
            You will see <strong>4 simulated emails in a work inbox</strong>. Imagine that each
            one was addressed to you at your job and handle it as you would in your actual inbox.
          </p>
          <p style={{ fontSize: '15px', marginTop: '0.5rem' }}>
            Your task is to decide how you would handle each email, <strong>exactly as you would in your real job</strong>.
          </p>
          <p style={{ fontSize: '15px', marginTop: '0.75rem' }}>
            <strong>Simulation note:</strong> Treat the dates and deadlines shown in each email as
            current within that simulated workday. Do not compare them with today&apos;s calendar date.
          </p>
        </div>

        <div className="instruction-section">
          <h2>📋 STEP-BY-STEP: What You Need To Do</h2>

          <div style={{ marginLeft: '1rem', marginTop: '1rem' }}>
            <p style={{ fontSize: '15px', marginBottom: '1rem' }}>
              <strong>STEP 1:</strong> You will see a list of 4 emails in an inbox (like Outlook or Gmail).
            </p>

            <p style={{ fontSize: '15px', marginBottom: '1rem' }}>
              <strong>STEP 2:</strong> <span style={{ backgroundColor: '#fff3cd', padding: '2px 6px' }}>Click on each email to open it and read the full message.</span>
            </p>

            <p style={{ fontSize: '15px', marginBottom: '1rem' }}>
              <strong>STEP 3:</strong> After reading the email, scroll down to the bottom of the email.
            </p>

            <p style={{ fontSize: '15px', marginBottom: '1rem' }}>
              <strong>STEP 4:</strong> You will see <strong>four buttons</strong> at the bottom. Click ONE of these buttons to make your decision.
            </p>

            <p style={{ fontSize: '15px', marginBottom: '1rem' }}>
              <strong>STEP 5:</strong> After clicking a button, you'll be asked a quick question about WHY you made that choice. Answer it briefly.
            </p>

            <p style={{ fontSize: '15px', marginBottom: '1rem' }}>
              <strong>STEP 6:</strong> Choose another email that does not yet have a decision. Repeat steps 2-5 for all 4 emails.
            </p>
          </div>
        </div>

        <div className="instruction-section" style={{ backgroundColor: '#fff9e6', padding: '1.5rem', borderRadius: '8px', marginTop: '1.5rem' }}>
          <h2>🔘 THE FOUR BUTTONS - What Each One Means</h2>
          <p style={{ marginBottom: '1rem' }}>At the bottom of every email, you will see these four buttons. Click ONE to make your decision:</p>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px', maxWidth: '600px', margin: '1.5rem auto' }}>
            {/* Engage Button - Exact replica */}
            <button style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              padding: '12px 8px',
              border: '2px solid #e1e5e9',
              borderRadius: '6px',
              background: '#ffffff',
              cursor: 'default',
              fontSize: '12px',
              gap: '4px',
              minHeight: '70px'
            }}>
              <span style={{ fontSize: '18px', marginBottom: '2px' }}>📧</span>
              <span style={{ textAlign: 'center', lineHeight: '1.2' }}>Engage</span>
            </button>

            {/* Verify Button - Exact replica */}
            <button style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              padding: '12px 8px',
              border: '2px solid #e1e5e9',
              borderRadius: '6px',
              background: '#ffffff',
              cursor: 'default',
              fontSize: '12px',
              gap: '4px',
              minHeight: '70px'
            }}>
              <span style={{ fontSize: '18px', marginBottom: '2px' }}>🔎</span>
              <span style={{ textAlign: 'center', lineHeight: '1.2' }}>Verify</span>
            </button>

            {/* Ignore Button - Exact replica */}
            <button style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              padding: '12px 8px',
              border: '2px solid #e1e5e9',
              borderRadius: '6px',
              background: '#ffffff',
              cursor: 'default',
              fontSize: '12px',
              gap: '4px',
              minHeight: '70px'
            }}>
              <span style={{ fontSize: '18px', marginBottom: '2px' }}>✓</span>
              <span style={{ textAlign: 'center', lineHeight: '1.2' }}>Ignore</span>
            </button>

            {/* Report Button - Exact replica */}
            <button style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              padding: '12px 8px',
              border: '2px solid #e1e5e9',
              borderRadius: '6px',
              background: '#ffffff',
              cursor: 'default',
              fontSize: '12px',
              gap: '4px',
              minHeight: '70px'
            }}>
              <span style={{ fontSize: '18px', marginBottom: '2px' }}>🚨</span>
              <span style={{ textAlign: 'center', lineHeight: '1.2' }}>Report</span>
            </button>
          </div>

          <div style={{ marginTop: '1rem', padding: '1rem', backgroundColor: '#ffffff', border: '1px solid #ddd', borderRadius: '6px' }}>
            <p style={{ fontSize: '14px', marginBottom: '0.5rem' }}>
              <strong>🔎 "Verify"</strong> - Click this if:
            </p>
            <ul style={{ marginLeft: '1.5rem', fontSize: '14px' }}>
              <li>You would contact the sender through a known phone number or separate channel</li>
              <li>You would open the official portal directly instead of using the email</li>
              <li>The request may be legitimate, but you would confirm it before acting</li>
            </ul>
          </div>

          <div style={{ marginTop: '1.5rem', padding: '1rem', backgroundColor: '#ffffff', border: '1px solid #ddd', borderRadius: '6px' }}>
            <p style={{ fontSize: '14px', marginBottom: '0.5rem' }}>
              <strong>📧 "Engage"</strong> - Click this if:
            </p>
            <ul style={{ marginLeft: '1.5rem', fontSize: '14px' }}>
              <li>You would click any links in the email</li>
              <li>You would open any attachments</li>
              <li>You would reply to this email</li>
              <li>You would follow the instructions in the email</li>
            </ul>
          </div>

          <div style={{ marginTop: '1rem', padding: '1rem', backgroundColor: '#ffffff', border: '1px solid #ddd', borderRadius: '6px' }}>
            <p style={{ fontSize: '14px', marginBottom: '0.5rem' }}>
              <strong>✓ "Ignore"</strong> - Click this if:
            </p>
            <ul style={{ marginLeft: '1.5rem', fontSize: '14px' }}>
              <li>You would take no action on the email</li>
              <li>You would leave it alone or delete it without interacting</li>
              <li>You would not contact the sender or report it for security review</li>
            </ul>
          </div>

          <div style={{ marginTop: '1rem', padding: '1rem', backgroundColor: '#ffffff', border: '1px solid #ddd', borderRadius: '6px' }}>
            <p style={{ fontSize: '14px', marginBottom: '0.5rem' }}>
              <strong>🚨 "Report"</strong> - Click this if:
            </p>
            <ul style={{ marginLeft: '1.5rem', fontSize: '14px' }}>
              <li>The email looks suspicious or untrustworthy</li>
              <li>You would use your organization's phishing or security-reporting process</li>
              <li>You would flag it for your IT or security team to review</li>
              <li>You would not engage with it while it is being reviewed</li>
            </ul>
          </div>

          <p style={{ fontSize: '13px', color: '#605e5c', textAlign: 'center', fontStyle: 'italic', marginTop: '1.5rem', fontWeight: 'bold' }}>
            ⚠️ IMPORTANT: You MUST click one of these four buttons for every email to proceed to the next one.
          </p>
        </div>

        <div className="instruction-section" style={{ backgroundColor: '#f0f4c3', padding: '1.5rem', borderRadius: '8px', marginTop: '1.5rem' }}>
          <h2>💡 REMEMBER</h2>
          <ul style={{ fontSize: '15px', lineHeight: '1.8' }}>
            <li><strong>This is YOUR work inbox</strong> - Act like you normally would at your real job</li>
            <li><strong>Click on each email</strong> to open it and read the full message</li>
            <li><strong>Click ONE of the four buttons</strong> at the bottom of every email</li>
            <li><strong>Answer the follow-up question</strong> after each decision</li>
            <li><strong>Complete all 4 emails</strong> to finish this task</li>
            <li><strong>No score will be shown during the task</strong> - choose what you would really do</li>
            <li><strong>Take your time</strong> - this is not a speed test</li>
          </ul>
        </div>

        <div className="instruction-section" style={{ marginTop: '1.5rem', textAlign: 'center', padding: '1rem', backgroundColor: '#e8f5e9', borderRadius: '8px' }}>
          <p style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '0.5rem' }}>
            ✅ Ready to start? Click the button below when you're ready to see the first email.
          </p>
          <p style={{ fontSize: '14px', color: '#555' }}>
            Time estimate: 10-15 minutes
          </p>
        </div>

        <div className="button-container">
          <button onClick={handleBegin} className="btn btn-primary">
            Begin Email Task
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmailTaskIntro;
